import React, { useRef, useState } from 'react';
import {
  Box, CircularProgress, IconButton, Menu, Tooltip, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { toast } from '../../Components/Toast';
import { appModalCloseButtonSx } from '../../Components/appModalChrome.styles';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import { colors } from '../../styles/theme';
import { downloadMetaResultsCsv } from '../../services/metaAdsService';
import { getMetaCsvErrorMessage } from '../metaAdsConfig';
import { saveBlobAs } from '../metaDownload';

/** Qué se puede bajar (las tres `rows` que acepta `getmetaresults?format=csv`). */
export const META_CSV_OPTIONS = [
  { rows: 'units', label: 'Descargar unidades (CSV)' },
  { rows: 'ads', label: 'Descargar anuncios (CSV)' },
  { rows: 'campaigns', label: 'Descargar campañas (CSV)' },
];
export const META_CSV_NOTE = 'Se descarga lo que ves en el período elegido.';

const menuPaperProps = {
  ...ACTION_MENU_PAPER_PROPS,
  sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 'auto', minWidth: '220px', maxWidth: 'min(320px, 92vw)' },
};
const triggerSx = {
  ...appModalCloseButtonSx,
  // Deshabilitado (bajando): gris legible.
  '&.Mui-disabled': { color: colors.disabledOnDarkText },
};
const noteSx = {
  px: 1.5, py: 1, fontSize: '11px', lineHeight: '15px', color: 'rgba(255,255,255,0.5)',
  borderTop: '1px solid rgba(255,255,255,0.08)',
};

/**
 * «Descargar» del Informe de la cuenta (t990u): ⋮ en el header del modal (junto a «Cerrar») con un
 * menú como el de Historial: unidades, anuncios o campañas del período elegido
 * (`getmetaresults?format=csv`, con JWT y como blob). Antes era una tarjeta al fondo del Informe.
 * Una descarga a la vez: mientras baja, el menú queda abierto con la opción elegida cargando y
 * las tres deshabilitadas (gris legible); al terminar se cierra. Si falla, el mismo mensaje por
 * `code` de siempre en un aviso (`toast.error`) y se puede reintentar.
 */
export default function MetaCsvDownloads({ period, ownerUserId = null, includeInactive = false }) {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(null);
  const lockRef = useRef(false);

  const download = async (option) => {
    if (lockRef.current) return;
    lockRef.current = true;
    setBusy(option.rows);
    let res;
    try {
      res = await downloadMetaResultsCsv({ rows: option.rows, period, includeInactive, ownerUserId });
    } catch (e) {
      res = { _httpOk: false, _httpStatus: null };
    }
    lockRef.current = false;
    setBusy(null);
    setAnchor(null);
    if (!res?._httpOk) {
      toast.error({ message: getMetaCsvErrorMessage(res) });
      return;
    }
    saveBlobAs(res.blob, res.filename);
    toast.success({ message: `Descargamos ${res.filename}.` });
  };

  return (
    <>
      <Tooltip title={<span>Descargar · período elegido</span>} placement="bottom" arrow describeChild>
        <IconButton
          size="small"
          aria-label="Descargar"
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          aria-busy={busy !== null || undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={triggerSx}
          data-testid="meta-report-downloads"
        >
          {busy !== null && !anchor
            ? <CircularProgress size={16} thickness={5} sx={{ color: colors.primary }} aria-label="Descargando" />
            : <MoreVertIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={menuPaperProps}
        MenuListProps={{ ...ACTION_MENU_LIST_PROPS, 'aria-label': 'Descargar' }}
      >
        {META_CSV_OPTIONS.map((o) => (
          <ActionMenuItem
            key={o.rows}
            icon={<FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />}
            label={o.label}
            loading={busy === o.rows}
            disabled={busy !== null}
            testId={`meta-report-csv-${o.rows}`}
            onClick={() => download(o)}
          />
        ))}
        <Box component="li" role="none" sx={noteSx}>
          <Typography component="span" sx={{ font: 'inherit', color: 'inherit' }} data-testid="meta-report-csv-note">{META_CSV_NOTE}</Typography>
        </Box>
      </Menu>
    </>
  );
}
